<template>
  <b-container v-if="token && name && hasRewards" class="inv-wrap">
    <div class="inv-card" :class="{ done: referred }">
      <div class="inv-main">
        <div class="inv-head">
          <span class="inv-avatar">{{ initials }}</span>
          <span class="inv-chip">Invitación personal</span>
        </div>

        <h3 class="inv-title" v-if="!referred">
          <strong>{{ name }}</strong> te invitó a Utopie
        </h3>
        <h3 class="inv-title" v-else>¡Listo! Tus usos ya están activos 🎉</h3>

        <p class="inv-sub" v-if="!referred">
          Inicia sesión y
          <strong
            >se activa{{ rewards.referred === 1 ? "" : "n" }}
            {{ usesLabel(rewards.referred) }} para ti y
            {{ usesLabel(rewards.referring) }} para {{ firstName }}</strong
          >, al instante.<br />
          Lo de abajo lo puedes ver gratis igual.
        </p>
        <p class="inv-sub" v-else>
          Ya sumaste tus usos y {{ firstName }} también. ¡A trabajar!
        </p>

        <div class="inv-badges" v-if="!referred">
          <span class="inv-badge">🎁 +{{ usesLabel(rewards.referred) }} para ti</span>
          <span class="inv-badge"
            >🎁 +{{ usesLabel(rewards.referring) }} para {{ firstName }}</span
          >
        </div>
      </div>

      <div class="inv-cta" v-if="!referred">
        <button type="button" class="inv-btn" @click="pedirSesion">
          Iniciar sesión y activar los usos
        </button>
        <p class="inv-hint">Con tu cuenta de Google · sin contraseñas</p>
      </div>
    </div>
  </b-container>
</template>

<script>
// Landing card for someone arriving through an invite link
// (`/utopie/#/<service>-demo?invita=<token>`). The token is the inviter's signed
// user id; it is kept in localStorage so it survives the login round-trip, and
// the referral is claimed the moment a session exists — whether the visitor logs
// in here or arrives already logged in.
import userService from "@/services/userService";
import { eventoLogin } from "@/services/analytics";
import { recordarOrigenLogin } from "@/services/origen";
import { DEMO_ROUTE } from "@/components/botpie-assistant/InviteFriendsView.vue";
import { hasReferralRewards } from "./referralRewards";

// De dónde salió este muro de login. Mismo agujero que tenía la tarjeta de
// golden ticket, y en la línea de arriba del mismo layout: las dos abrían el
// modal sin anunciar muro, así que el login que venía después salía sin
// `origen_login` —o heredando el de un muro de demo visto antes en la misma
// carga, que le atribuye a la demo una conversión que trajo la invitación.
const ORIGEN_LOGIN = "invitacion";

const STORAGE_KEY = "invite_token";
// El servicio se guarda JUNTO al token y no se recalcula al reclamar.
//
// El token sobrevive al ida y vuelta del login, y para cuando hay sesion la
// persona puede estar en otra demo —o en la portada, si volvio del login por
// la ruta generica—. Lo que se premia es el servicio al que la invitaron, que
// es el de la ruta del link, no aquel donde termino parada.
const SERVICE_STORAGE_KEY = "invite_service";
const SERVICE_BY_ROUTE = Object.entries(DEMO_ROUTE).reduce(
  (acc, [service, route]) => ({ ...acc, [route]: service }),
  {}
);

export default {
  name: "InvitationBanner",
  data() {
    return {
      token: "",
      service: "",
      name: "",
      firstName: "",
      // Zeros, not an optimistic guess: if `inviter_info` ever answers without
      // rewards, the card must stay hidden rather than promise a use we never
      // grant.
      rewards: { referring: 0, referred: 0, each_five: 0 },
      referred: false,
    };
  },
  created() {
    const fromLink = this.$route.query.invita;
    const token = fromLink || localStorage.getItem(STORAGE_KEY);
    if (!token) return;
    localStorage.setItem(STORAGE_KEY, token);
    if (fromLink) {
      localStorage.setItem(
        SERVICE_STORAGE_KEY,
        SERVICE_BY_ROUTE[this.$route.name] || ""
      );
    }
    this.token = token;
    this.service = localStorage.getItem(SERVICE_STORAGE_KEY) || "";
    userService.inviterInfo(token, this.service).then(
      (data) => {
        this.name = data.name;
        this.firstName = data.first_name || data.name;
        if (data.rewards) this.rewards = data.rewards;
        this.anunciarMuro();
        this.claim();
      },
      // A stale or tampered token shows nothing at all — never a broken card.
      () => this.forget()
    );
  },
  watch: {
    // The visitor logs in with the card on screen: claim without a reload.
    "$store.state.login.token"(value) {
      if (value) this.claim();
    },
  },
  computed: {
    hasRewards() {
      return hasReferralRewards(this.rewards);
    },
    initials() {
      return this.name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
    },
  },
  methods: {
    // El muro se anuncia cuando la tarjeta VA A PINTAR su botón de entrar:
    // con sesión iniciada `claim()` reclama solo y nadie ve ningún muro, y
    // sin premios la tarjeta no aparece (`v-if` de arriba).
    anunciarMuro() {
      if (this.$store.state.login.token || this.referred) return;
      if (!this.hasRewards) return;
      eventoLogin("login_wall_shown", { origen_login: ORIGEN_LOGIN },
                  this.service);
    },
    pedirSesion() {
      // Otra vez, como en TicketBanner: `olvidarOrigenLogin()` corre al
      // cerrarse cualquier modal, y entre que la tarjeta pinta y la persona
      // aprieta puede haberse abierto y cerrado otro.
      recordarOrigenLogin(ORIGEN_LOGIN);
      this.$emit("open-login");
    },
    usesLabel(amount) {
      const n = Number(amount) || 0;
      return `${n} ${n === 1 ? "uso" : "usos"}`;
    },
    forget() {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SERVICE_STORAGE_KEY);
      this.token = "";
    },
    // Claiming is one-shot per account: the backend rejects a second referral
    // (and a self-referral) on its own, so a rejection just drops the token.
    //
    // Which is exactly why we don't claim while the amounts are 0. The
    // `ReferenceUser` row is permanent and there is no second chance, so
    // claiming a dormant referral would spend the visitor's only one on
    // nothing — and the day we turn the amounts on, they could never claim
    // again. Keeping the token parked in localStorage costs nothing and lets
    // the same link pay out once the program is really on.
    claim() {
      if (this.referred || !this.token || !this.$store.state.login.token) return;
      if (!this.hasRewards) return;
      // `service` decide en que servicio caen los usos: el backend paga uno
      // solo, el de la invitacion, y es el mismo que `inviter_info` uso para
      // armar la promesa que esta tarjeta muestra.
      userService.referUser({ token: this.token, service: this.service }).then(
        () => {
          this.referred = true;
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(SERVICE_STORAGE_KEY);
          this.$toasted.success("¡Usos activados para los dos! 🎁");
        },
        () => this.forget()
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.inv-wrap {
  margin-bottom: 1.5rem;
}
.inv-card {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  border: 2px dashed #2bb3a3;
  border-radius: 18px;
  background: #f4fbf8;
  padding: 1.5rem;
  text-align: left;
  &.done {
    border-style: solid;
  }
}
.inv-main {
  flex: 1 1 320px;
}
.inv-head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.75rem;
}
.inv-avatar {
  width: 2.6rem;
  height: 2.6rem;
  border-radius: 50%;
  background: #f5a623;
  color: white;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.inv-chip {
  border: 1px solid #2bb3a3;
  border-radius: 999px;
  padding: 0.15rem 0.75rem;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #17796d;
}
.inv-title {
  font-weight: 400;
  margin-bottom: 0.5rem;
  strong {
    color: #17796d;
  }
}
.inv-sub {
  margin-bottom: 0.75rem;
}
.inv-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.inv-badge {
  border: 1px solid #d7e5e1;
  border-radius: 10px;
  padding: 0.35rem 0.75rem;
  background: white;
  font-size: 0.9rem;
}
.inv-cta {
  flex: 0 1 320px;
  text-align: center;
}
.inv-btn {
  width: 100%;
  border: 1px solid #2bb3a3;
  border-radius: 12px;
  background: white;
  color: #17796d;
  font-weight: 700;
  padding: 0.9rem 1rem;
}
.inv-hint {
  font-size: 0.8rem;
  color: #6c7b78;
  margin: 0.5rem 0 0;
}
</style>
